import * as _ from 'lodash';

// Never sent to updatePartialProposal: identity, values computed when the proposal
// is fetched and save metadata that only lives on the client.
const CLIENT_ONLY_FIELDS = new Set([
  '_id',
  'id',
  'pid',
  'uid',
  'auid',
  'cid',
  'pnum',
  'cursorId',
  'channel',
  'name',
  'email',
  'date',
  'history',
  'decimalPrecision',
  'chatwayScripts',
  'creatorUserway',
  'quotes',
  'isManager',
  'isOwner',
  'noTemplate',
  'from',
  'templateUrl',
  'whatChanged',
  'partialChanges',
  'backupId',
  'edited',
  'deletedSections',
  'draftRev',
  '__typename',
]);

// Draft keys holding proposal settings rather than section content; never deleted as sections.
const DRAFT_SETTINGS_KEYS = new Set([
  'sectionorder',
  'lastSectionOrder',
  'header',
  'signature',
  'variables',
  'titleFont',
  'subTitleFont',
  'bodyFont',
  'sectionStyles',
  'layoutConfig',
  'tocExclude',
  'proposalExclude',
  'disableSig',
]);

/**
 * Section keys that can be removed from the draft: not a settings key and no longer
 * in the section order.
 *
 * @param {string[]} keys - sections the caller deleted
 * @param {string[]} sectionorder - section order after the delete
 * @returns {string[]} keys safe to remove
 */
export const getDeletableSectionKeys = (keys, sectionorder) =>
  _.uniq(
    (keys || []).filter(
      (key) =>
        typeof key === 'string' &&
        key &&
        !DRAFT_SETTINGS_KEYS.has(key) &&
        !(sectionorder || []).includes(key)
    )
  );

/**
 * updatePartialProposal change removing deleted sections from the draft.
 *
 * @param {string[]} keys - section keys to remove
 * @returns {object[]} change list, empty when nothing is removed
 */
export const buildSectionDeletes = (keys) =>
  keys?.length
    ? [{ type: 'delete', data: Object.fromEntries(keys.map((key) => [`draft.${key}`, ''])) }]
    : [];

// Owned by status changes, prospects and CRM syncs, which updateProposal handles.
// Left out of partial saves unless a caller sets them, which switches that save to
// updateProposal.
export const LEGACY_ONLY_FIELDS = new Set([
  'state',
  'prevState',
  'published',
  'lastStatusUpdateTime',
  'signature',
  'openLogs',
  'emailLogs',
  'seenCount',
  'seenTime',
  'lastSeen',
  'payment',
  'payedfor',
  'selectedOption',
  'approvedmanually',
  'emailSettings',
  'sharedViaMail',
  'isScheduled',
  'spamUrls',
  'pipedriveDealId',
  'hubspot',
  'wixProposalId',
  'wixCustomerId',
  'deletedAt',
  'lastSelfDownload',
  'datesent',
  'pdflink',
  'tags',
  'lockedData',
]);

// The server reads these together to log the end of an editing session, so they are
// sent whenever a caller sets them, even if unchanged.
const ALWAYS_SENT_FIELDS = new Set(['isEditing', 'editEvent']);

/**
 * Builds the updatePartialProposal data for an editor save by comparing the proposal
 * being saved with a deep copy of the last state synced with the server. Comparing
 * against that copy (not propRef) keeps changes made in place on the proposal object.
 *
 * @param {object} params
 * @param {object} params.changes - fields the caller passed for this save
 * @param {object} params.proposal - full proposal being saved
 * @param {object} params.synced - last state synced with the server
 * @returns {{ useLegacy: boolean, data: object }} dot paths to values, or useLegacy
 * when the save has to go through updateProposal
 */
export const buildPartialProposalChanges = ({ changes = {}, proposal = {}, synced }) => {
  if (!synced || synced._id !== proposal._id) {
    return { useLegacy: true, data: {} };
  }

  const setsLegacyField = Object.keys(changes).some(
    (field) =>
      LEGACY_ONLY_FIELDS.has(field) &&
      proposal[field] !== undefined &&
      !_.isEqual(proposal[field], synced[field])
  );

  if (setsLegacyField) {
    return { useLegacy: true, data: {} };
  }

  const data = {};

  Object.keys(proposal).forEach((field) => {
    const value = proposal[field];

    if (
      field === 'draft' ||
      value === undefined ||
      CLIENT_ONLY_FIELDS.has(field) ||
      LEGACY_ONLY_FIELDS.has(field)
    ) {
      return;
    }

    const alwaysSent = ALWAYS_SENT_FIELDS.has(field) && field in changes;

    if (alwaysSent || !_.isEqual(value, synced[field])) {
      data[field] = value;
    }
  });

  const draft = proposal.draft || {};
  const syncedDraft = synced.draft || {};

  Object.keys(draft).forEach((key) => {
    if (draft[key] !== undefined && !_.isEqual(draft[key], syncedDraft[key])) {
      data[`draft.${key}`] = draft[key];
    }
  });

  return {
    useLegacy: false,
    data,
    deletes: getDeletableSectionKeys(changes.deletedSections, proposal.draft?.sectionorder),
  };
};

/**
 * updateProposal input for an editor save that can't be partial: every field the caller
 * set, plus the fields and draft sections that differ from the last state synced with the
 * server. Sending the whole proposal wrote back this tab's copy of sections and prospect
 * activity (openLogs, seenCount...) that had changed elsewhere since. Without a synced copy
 * of the same proposal the whole proposal is sent, as before.
 *
 * @param {object} params
 * @param {object} params.changes - fields the caller passed for this save
 * @param {object} params.proposal - full proposal being saved
 * @param {object} params.synced - last state synced with the server
 * @returns {object} updateProposal input
 */
export const buildLegacyProposalInput = ({ changes = {}, proposal = {}, synced }) => {
  if (!synced || synced._id !== proposal._id) {
    return { id: proposal._id, ...proposal };
  }

  const input = { id: proposal._id };

  Object.keys(proposal).forEach((field) => {
    const value = proposal[field];

    if (field === 'draft' || value === undefined) {
      return;
    }

    const changedHere = !CLIENT_ONLY_FIELDS.has(field) && !_.isEqual(value, synced[field]);

    // the server uses channel for the update it publishes
    if (field === 'channel' || field in changes || changedHere) {
      input[field] = value;
    }
  });

  const draft = proposal.draft || {};
  const syncedDraft = synced.draft || {};
  const changedDraft = {};

  Object.keys(draft).forEach((key) => {
    if (draft[key] !== undefined && !_.isEqual(draft[key], syncedDraft[key])) {
      changedDraft[key] = draft[key];
    }
  });

  if (Object.keys(changedDraft).length) {
    input.draft = changedDraft;
  }

  return input;
};

/**
 * Returns the synced state after the server accepted a partial save.
 *
 * @param {object} synced - last state synced with the server
 * @param {object} data - dot paths that were saved
 * @param {object} serverProposal - updatePartialProposal response
 * @param {string[]} deletes - section keys removed from the draft
 * @returns {object} new synced state
 */
export const applyPartialSave = (synced, data, serverProposal = {}, deletes = []) => {
  const next = { ...synced, draft: _.omit(synced.draft || {}, deletes) };

  Object.entries(data).forEach(([path, value]) => {
    if (path.startsWith('draft.')) {
      next.draft[path.slice('draft.'.length)] = _.cloneDeep(value);
    } else {
      next[path] = _.cloneDeep(value);
    }
  });

  if (serverProposal.draft?.variables) {
    next.draft.variables = _.cloneDeep(serverProposal.draft.variables);
  }

  ['editEvent', 'edited', 'draftRev'].forEach((field) => {
    if (serverProposal[field] !== undefined) {
      next[field] = serverProposal[field];
    }
  });

  return next;
};

/**
 * Merges a server proposal (full or with a partial draft) into the synced state.
 *
 * @param {object} synced - last state synced with the server
 * @param {object} serverProposal - proposal returned by the server
 * @returns {object} new synced state
 */
export const mergeServerProposal = (synced, serverProposal) => {
  if (!synced || !serverProposal) {
    return synced;
  }

  const { draft, ...fields } = _.cloneDeep(serverProposal);

  return { ...synced, ...fields, draft: { ...(synced.draft || {}), ...(draft || {}) } };
};

/**
 * Applies a proposal the server pushed (full or with a partial draft) to the editor's
 * copy. Fields and draft keys this tab changed but hasn't saved yet (its copy differs
 * from the synced one) keep the local value, so a push can't wipe edits still being
 * saved; the rest take the pushed value. The pushed values become the synced copy either
 * way, since they are what the server stores.
 *
 * @param {object} params
 * @param {object} params.local - the editor's proposal
 * @param {object} params.synced - last state synced with the server
 * @param {object} params.pushed - proposal from the server
 * @returns {{ local: object, synced: object }} new editor and synced copies
 */
export const mergePushedProposal = ({ local, synced, pushed }) => {
  const { draft: pushedDraft, ...pushedFields } = pushed;
  const nextLocal = { ...local, draft: { ...(local.draft || {}) } };
  const nextSynced = { ...synced, draft: { ...(synced.draft || {}) } };

  Object.entries(pushedFields).forEach(([field, value]) => {
    if (_.isEqual(local[field], synced[field])) {
      nextLocal[field] = value;
    }
    nextSynced[field] = _.cloneDeep(value);
  });

  Object.entries(pushedDraft || {}).forEach(([key, value]) => {
    if (_.isEqual(local.draft?.[key], synced.draft?.[key])) {
      nextLocal.draft[key] = value;
    }
    nextSynced.draft[key] = _.cloneDeep(value);
  });

  return { local: nextLocal, synced: nextSynced };
};

/**
 * Short description of what changed, sent as whatChanged: the server only needs to
 * know one was provided, and the full object can be as big as the proposal.
 *
 * @param {object} whatChanged - what the caller changed
 * @returns {object|undefined} changed keys
 */
export const summarizeWhatChanged = (whatChanged) =>
  whatChanged ? { keys: Object.keys(whatChanged).slice(0, 50) } : undefined;

/**
 * Combines the fields callers passed for saves batched into one request. The latest
 * proposal already holds every earlier change; this keeps which fields were set (for
 * presence and updateProposal fallback) and what changed across the batch.
 *
 * @param {object} earlier - changes of the saves already batched
 * @param {object} later - changes of the save joining the batch
 * @returns {object} combined changes
 */
export const mergeBatchedChanges = (earlier = {}, later = {}) => {
  const whatChanged =
    earlier.whatChanged || later.whatChanged
      ? { ...(earlier.whatChanged || {}), ...(later.whatChanged || {}) }
      : undefined;

  const deletedSections = _.uniq([
    ...(earlier.deletedSections || []),
    ...(later.deletedSections || []),
  ]);

  return {
    ...earlier,
    ...later,
    ...(whatChanged ? { whatChanged } : {}),
    ...(deletedSections.length ? { deletedSections } : {}),
  };
};
